import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Box, Loader2, LogOut, Mail, Package, ExternalLink, ShoppingBasket, LayoutGrid } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../components/ui/sheet";
import { Button } from "../components/ui/button";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useDuckActions } from "../hooks/useDuckActions";
import { DuckCard, fmtDate } from "./DuckCard";
import { DuckDownloadModal } from "./DuckDownloadModal";

const OrderHistory = ({ orders }) =>
  orders.length > 0 && (
    <div data-testid="order-history" className="mt-8">
      <p className="flex items-center gap-2 text-xs font-mono2 uppercase tracking-widest text-muted-foreground mb-3">
        <Package className="h-4 w-4" /> Order history
      </p>
      <div className="grid gap-2">
        {orders.map((o, i) => (
          <div key={`${o.duck_id}-${i}`} data-testid={`order-row-${i}`} className="rounded-2xl border border-border bg-card/60 p-3 flex items-center gap-3">
            <span className="grid place-items-center h-9 w-9 rounded-full bg-accent/15 text-accent shrink-0"><Package className="h-4 w-4" /></span>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold truncate">
                {o.duck_name || "Custom duck"}
                {o.order_name && <span className="ml-1.5 font-mono2 text-xs text-muted-foreground font-normal">{o.order_name}</span>}
              </p>
              <p className="text-xs text-muted-foreground font-mono2" data-testid={`order-status-${i}`}>
                {o.label}{o.height ? ` · ${o.height} mm` : ""}{o.quantity > 1 ? ` · ×${o.quantity}` : ""}{o.created_at ? ` · ${fmtDate(o.created_at)}` : ""}
              </p>
            </div>
            {o.tracking_url && (
              <a href={o.tracking_url} target="_blank" rel="noreferrer" data-testid={`order-track-${i}`} className="shrink-0 inline-flex items-center gap-1 text-xs font-semibold text-accent hover:underline">
                Track <ExternalLink className="h-3.5 w-3.5" />
              </a>
            )}
          </div>
        ))}
      </div>
    </div>
  );

export const LoadMoreRow = ({ shown, total, hasMore, loading, onMore, step, testId = "gallery-load-more-btn" }) => (
  <div className="mt-4 flex flex-col items-center gap-2">
    <p className="text-xs font-mono2 text-muted-foreground" data-testid={`${testId}-count`}>{shown} of {total}</p>
    {hasMore && (
      <Button data-testid={testId} variant="outline" className="rounded-full gap-2" disabled={loading} onClick={onMore}>
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Load {step} more
      </Button>
    )}
  </div>
);

// The slide-over "My Ducks" sheet: the first 10 ducks (cached) + load-more and lazy order history. Archived ducks and
// flocks live on the Your Ducks page, reached with the orange "Your Ducks (N)" button.
export const YOUR_DUCKS_BTN = "bg-accent text-accent-foreground hover:bg-accent/90 hover:text-accent-foreground";   // orange, white like every orange button
export const BASKET_BTN = "bg-primary text-primary-foreground hover:bg-primary/90";     // yellow, dark text: 9.9:1

export const SessionGallery = ({ open, onOpenChange, items, total, hasMore, loadingMore, onLoadMore, onOpenItem, onDelete, onReload, onRenamed }) => {
  const { user, signOut } = useAuth();
  const [orders, setOrders] = useState([]);
  const actions = useDuckActions({ onOpenItem, onDelete, onRenamed, onReload: () => onReload?.() });

  // Order history only for the ducks on screen, fetched when the sheet opens (never on Studio mount).
  const shownIds = items.map((c) => c.id).join(",");
  useEffect(() => {
    if (!open || !user || !shownIds) return;
    api.get("/creations/orders/mine", { params: { ids: shownIds } }).then((r) => setOrders(r.data)).catch(() => setOrders([]));
  }, [open, user, shownIds]);

  const readyLoaded = items.filter((c) => c.has_model && c.status === "approved");

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <DuckDownloadModal actions={actions} />
      <SheetContent className="w-full sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="font-heading text-2xl flex items-center gap-2"><Box className="h-5 w-5 text-accent" /> My Ducks</SheetTitle>
          <SheetDescription>Every duck saved to your profile. Re-open, re-download or share any of them.</SheetDescription>
        </SheetHeader>

        {user && (
          <div data-testid="gallery-account" className="mt-4 rounded-2xl border border-border bg-card/60 p-3 flex items-center justify-between gap-3">
            <span className="flex items-center gap-2 min-w-0">
              <span className="grid place-items-center h-9 w-9 rounded-full bg-accent text-accent-foreground font-bold text-sm shrink-0">{(user.email || "?").charAt(0).toUpperCase()}</span>
              <span className="min-w-0">
                <span className="block text-xs text-muted-foreground font-mono2 flex items-center gap-1"><Mail className="h-3 w-3" /> Signed in</span>
                <span className="block text-sm font-semibold truncate" data-testid="gallery-account-email">{user.email}</span>
              </span>
            </span>
            <Button variant="ghost" size="sm" onClick={() => signOut()} data-testid="gallery-signout-btn" className="rounded-full gap-1.5 text-destructive shrink-0"><LogOut className="h-4 w-4" /> Sign out</Button>
          </div>
        )}

        {items.length === 0 ? (
          <div className="mt-16 text-center text-muted-foreground"><span className="text-5xl block mb-4">🦆</span>No ducks yet — go make your first one!</div>
        ) : (
          <>
            <Link to="/my-ducks" onClick={() => onOpenChange(false)} className="block mt-6">
              <Button data-testid="gallery-view-all-btn" className={`w-full rounded-full gap-2 font-bold ${YOUR_DUCKS_BTN}`}><LayoutGrid className="h-4 w-4" /> Your Ducks ({total})</Button>
            </Link>
            {readyLoaded.length >= 2 && (
              <Button data-testid="gallery-basket-all-btn" className={`mt-3 w-full rounded-full gap-2 font-bold ${BASKET_BTN}`} disabled={actions.basketBusy} onClick={() => actions.basketAdd(readyLoaded.slice(0, 24).map((c) => c.id))}>
                <ShoppingBasket className="h-4 w-4" /> Add the {Math.min(readyLoaded.length, 24)} print-ready ducks shown to the basket
              </Button>
            )}
            <div data-testid="session-gallery-grid" className="mt-6 grid grid-cols-1 gap-4">
              {items.map((c, i) => <DuckCard key={c.id} c={c} i={i} actions={actions} />)}
            </div>
            <LoadMoreRow shown={items.length} total={total} hasMore={hasMore} loading={loadingMore} onMore={onLoadMore} step={10} />
            <OrderHistory orders={orders} />
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
